/**
 * Center-type wording, applied to the whole translation bundle.
 *
 * The translation files are written for a learning center (students, lessons,
 * teachers). Other center types call the same things by other names:
 *
 *   sport     trainees, training sessions, coaches
 *   clinics   clients, sessions, staff (therapy, hair transplant, pharma clinic)
 *
 * Re-labelling key by key (useSmartTranslation) only ever covered the keys
 * someone remembered, so every screen that used plain t() still said
 * "students". Instead, when the session's center type is known, each loaded
 * language bundle is rewritten once with that type's word rules and every t()
 * in the app reads the right words. Switching center restores the original
 * bundle first, so a teaching center never sees sport wording.
 */

// An Arabic word may carry one attached particle (و ف ب ك ل) and the article
// (ال, or لل after ل). A Hebrew word may carry the prefixes ו ה ב ל מ ש כ.
// Word edges are LETTERS only: Arabic punctuation (، ؛ ؟) and harakat sit in
// the same Unicode block and must not hide a word ("كل العملاء،").
const AR_LETTER = '[\\u0621-\\u063A\\u0641-\\u064A\\u0671-\\u06D3]';
const AR_BEFORE = `(^|(?!${AR_LETTER}).)`;
const AR_AFTER = `(?!${AR_LETTER})`;
const HE_BEFORE = '(^|[^\\u05D0-\\u05EA])';
const HE_AFTER = '(?![\\u05D0-\\u05EA])';
// Attached pronouns: لطلابك، معلمه، عميلنا.
const AR_PRONOUN = '(كم|كن|ك|هم|هن|ها|ه|نا|ي)?';

/** Arabic rule: [definite form, indefinite form] → [definite, indefinite]. */
function arWord(fromDef, fromIndef, toDef, toIndef) {
  const stemFrom = fromDef.replace(/^ال/, '');
  const stemTo = toDef.replace(/^ال/, '');
  // A pronoun can only follow a one-word replacement ("جلسة تدريبيةك" is not
  // Arabic). "مدرسه" is also colloquial for "school", so مدرس takes none.
  const oneWord = !/\s/.test(toIndef) && fromIndef !== 'مدرس';
  // Sound plurals drop their nūn before a pronoun: متدربين + ك → متدربيك.
  // A final hamza sits on yā' before one: عملاء + ك → عملائك.
  const stemBefore = (p) => {
    if (!p) return toIndef;
    if (/ين$/.test(toIndef)) return toIndef.slice(0, -1);
    if (/اء$/.test(toIndef)) return `${toIndef.slice(0, -1)}ئ`;
    return toIndef;
  };
  // A two-word replacement takes the pronoun on its noun and the article on
  // its adjective: حصصك → جلساتك التدريبية, حصتك… (ة → ت before a pronoun).
  const multiWord = (pron) => {
    if (!pron) return toIndef;
    const [head, ...tail] = toIndef.split(' ');
    return [`${head.replace(/ة$/, 'ت')}${pron}`, ...tail.map((w) => `ال${w}`)].join(' ');
  };
  // "my" on a sound plural: متدربين + ي → متدربيّ.
  const withPronoun = (pre, particle, pron) => (pron === 'ي' && /ين$/.test(toIndef)
    ? `${pre}${particle}${toIndef.slice(0, -1)}ّ`
    : `${pre}${particle}${stemBefore(pron)}${pron || ''}`);
  // Accusative indefinite (عميلاً → متدرباً); a ة ending takes no alef.
  // Sound plurals (‑ين/‑ون) take no tanween at all.
  const accTo = /(ين|ون)$/.test(toIndef) ? toIndef : (/ة$/.test(toIndef) ? `${toIndef}ً` : `${toIndef}اً`);
  return [
    // لل + stem (للطلاب → للمتدربين)
    { re: new RegExp(`${AR_BEFORE}([وف]?)لل${stemFrom}${AR_AFTER}`, 'g'), to: `$1$2لل${stemTo}` },
    // [particle] ال + stem (والطلاب، بالدرس، الطلاب)
    { re: new RegExp(`${AR_BEFORE}([وف]?[بك]?)ال${stemFrom}${AR_AFTER}`, 'g'), to: `$1$2ال${stemTo}` },
    { re: new RegExp(`${AR_BEFORE}([وف]?[بكل]?)${fromIndef}اً${AR_AFTER}`, 'g'), to: `$1$2${accTo}` },
    // [particle] indefinite [+ pronoun] (طلاب، وطالب، لطالب، لطلابك)
    fromIndef === 'مدرس'
      ? { re: new RegExp(`${AR_BEFORE}([وف]?[بكل]?)${fromIndef}${AR_AFTER}`, 'g'), to: `$1$2${toIndef}` }
      : oneWord
        ? { re: new RegExp(`${AR_BEFORE}([وف]?[بكل]?)${fromIndef}${AR_PRONOUN}${AR_AFTER}`, 'g'), to: (m, pre, particle, pron) => withPronoun(pre, particle, pron) }
        : { re: new RegExp(`${AR_BEFORE}([وف]?[بكل]?)${fromIndef}${AR_PRONOUN}${AR_AFTER}`, 'g'), to: (m, pre, particle, pron) => `${pre}${particle}${multiWord(pron)}` },
  ];
}

/**
 * Arabic dual of حصة, which the حصة rule cannot see: the plural forms of
 * "create 2 lessons" read "إنشاء حصتين" (accusative / genitive) and of
 * "2 past lessons stay" read "حصتان سابقتان تبقيان" (nominative). Each case
 * maps to its own target: `noun` / `adj` for حصتين, `nounNom` / `adjNom` for
 * حصتان. The article goes on both words.
 */
function arDual(noun, adj, nounNom, adjNom) {
  const rule = (from, n, a) => ({
    re: new RegExp(`${AR_BEFORE}([وف]?[بكل]?)(ال)?${from}${AR_AFTER}`, 'g'),
    to: a ? `$1$2$3${n} $3${a}` : `$1$2$3${n}`,
  });
  return [rule('حصتين', noun, adj), rule('حصتان', nounNom, adjNom)];
}

function heWord(from, to) {
  // שמורה/שמורים/שמורות is "saved/reserved", not ש + מורה.
  const prefix = /^מור/.test(from) ? '(ו?[הבלמכ]?)' : '([וש]?[הבלמכ]?)';
  return [{ re: new RegExp(`${HE_BEFORE}${prefix}${from}${HE_AFTER}`, 'g'), to: `$1$2${to}` }];
}

function enWord(from, to) {
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  return [
    { re: new RegExp(`\\b${from}\\b`, 'g'), to },
    { re: new RegExp(`\\b${cap(from)}\\b`, 'g'), to: cap(to) },
    { re: new RegExp(`\\b${from.toUpperCase()}\\b`, 'g'), to: to.toUpperCase() },
  ];
}

// Order matters: longer / plural forms before singular ones.
const SPORT = {
  ar: [
    ...arWord('الطالبات', 'طالبات', 'المتدربات', 'متدربات'),
    ...arWord('الطالبة', 'طالبة', 'المتدربة', 'متدربة'),
    ...arWord('الطلاب', 'طلاب', 'المتدربين', 'متدربين'),
    ...arWord('الطلبة', 'طلبة', 'المتدربين', 'متدربين'),
    ...arWord('الطالب', 'طالب', 'المتدرب', 'متدرب'),
    ...arWord('العملاء', 'عملاء', 'المتدربين', 'متدربين'),
    ...arWord('العميل', 'عميل', 'المتدرب', 'متدرب'),
    ...arDual('جلستين', 'تدريبيتين', 'جلستان', 'تدريبيتان'),
    ...arWord('الحصص', 'حصص', 'الجلسات التدريبية', 'جلسات تدريبية'),
    ...arWord('الحصة', 'حصة', 'الجلسة التدريبية', 'جلسة تدريبية'),
    ...arWord('الدروس', 'دروس', 'الجلسات التدريبية', 'جلسات تدريبية'),
    ...arWord('الدرس', 'درس', 'الجلسة التدريبية', 'جلسة تدريبية'),
    ...arWord('المعلمين', 'معلمين', 'المدربين', 'مدربين'),
    ...arWord('المعلمون', 'معلمون', 'المدربون', 'مدربون'),
    ...arWord('المعلمة', 'معلمة', 'المدربة', 'مدربة'),
    ...arWord('المعلم', 'معلم', 'المدرب', 'مدرب'),
    ...arWord('المدرسين', 'مدرسين', 'المدربين', 'مدربين'),
    ...arWord('المدرس', 'مدرس', 'المدرب', 'مدرب'),
    // Bare "session(s)" becomes "training session(s)" — but not twice.
    { re: new RegExp(`${AR_BEFORE}([وف]?[بك]?)الجلسات(?! التدريبية)${AR_AFTER}`, 'g'), to: '$1$2الجلسات التدريبية' },
    { re: new RegExp(`${AR_BEFORE}([وف]?[بكل]?)جلسات(?! تدريبية)${AR_AFTER}`, 'g'), to: '$1$2جلسات تدريبية' },
    { re: new RegExp(`${AR_BEFORE}([وف]?[بك]?)الجلسة(?! التدريبية)${AR_AFTER}`, 'g'), to: '$1$2الجلسة التدريبية' },
    { re: new RegExp(`${AR_BEFORE}([وف]?[بكل]?)جلسة(?! تدريبية)${AR_AFTER}`, 'g'), to: '$1$2جلسة تدريبية' },
  ],
  he: [
    ...heWord('תלמידות', 'מתאמנות'),
    ...heWord('תלמידים', 'מתאמנים'),
    ...heWord('תלמידה', 'מתאמנת'),
    ...heWord('תלמיד', 'מתאמן'),
    ...heWord('לקוחות', 'מתאמנים'),
    ...heWord('לקוח', 'מתאמן'),
    ...heWord('שיעורים', 'אימונים'),
    ...heWord('שיעור', 'אימון'),
    ...heWord('מפגשים', 'אימונים'),
    ...heWord('מפגש', 'אימון'),
    ...heWord('מורים', 'מאמנים'),
    ...heWord('מורות', 'מאמנות'),
    ...heWord('מורה', 'מאמן'),
  ],
  en: [
    ...enWord('students', 'trainees'),
    ...enWord('student', 'trainee'),
    ...enWord('clients', 'trainees'),
    ...enWord('client', 'trainee'),
    ...enWord('lessons', 'training sessions'),
    ...enWord('lesson', 'training session'),
    ...enWord('classes', 'training sessions'),
    ...enWord('teachers', 'coaches'),
    ...enWord('teacher', 'coach'),
    { re: /\b(?<![Tt]raining )sessions\b/g, to: 'training sessions' },
    { re: /\b(?<![Tt]raining )session\b/g, to: 'training session' },
    { re: /\b(?<![Tt]raining )Sessions\b/g, to: 'Training sessions' },
    { re: /\b(?<![Tt]raining )Session\b/g, to: 'Training session' },
  ],
};

const CLINIC = {
  ar: [
    ...arWord('الطالبات', 'طالبات', 'العميلات', 'عميلات'),
    ...arWord('الطالبة', 'طالبة', 'العميلة', 'عميلة'),
    ...arWord('الطلاب', 'طلاب', 'العملاء', 'عملاء'),
    ...arWord('الطلبة', 'طلبة', 'العملاء', 'عملاء'),
    ...arWord('الطالب', 'طالب', 'العميل', 'عميل'),
    ...arDual('جلستين', null, 'جلستان'),
    ...arWord('الحصص', 'حصص', 'الجلسات', 'جلسات'),
    ...arWord('الحصة', 'حصة', 'الجلسة', 'جلسة'),
    ...arWord('الدروس', 'دروس', 'الجلسات', 'جلسات'),
    ...arWord('الدرس', 'درس', 'الجلسة', 'جلسة'),
    // Clinic staff are therapists/doctors/workers — "staff member" fits all.
    ...arWord('المعلمين', 'معلمين', 'الموظفين', 'موظفين'),
    ...arWord('المعلمون', 'معلمون', 'الموظفون', 'موظفون'),
    ...arWord('المعلمات', 'معلمات', 'الموظفات', 'موظفات'),
    ...arWord('المعلمة', 'معلمة', 'الموظفة', 'موظفة'),
    ...arWord('المعلم', 'معلم', 'الموظف', 'موظف'),
  ],
  he: [
    ...heWord('תלמידות', 'לקוחות'),
    ...heWord('תלמידים', 'לקוחות'),
    ...heWord('תלמידה', 'לקוחה'),
    ...heWord('תלמיד', 'לקוח'),
    ...heWord('שיעורים', 'מפגשים'),
    ...heWord('שיעור', 'מפגש'),
    ...heWord('מורים', 'עובדים'),
    ...heWord('מורות', 'עובדות'),
    ...heWord('מורה', 'עובד'),
  ],
  en: [
    ...enWord('students', 'clients'),
    ...enWord('student', 'client'),
    ...enWord('lessons', 'sessions'),
    ...enWord('lesson', 'session'),
    ...enWord('teachers', 'staff'),
    ...enWord('teacher', 'staff member'),
  ],
};

/** Which rule set a session gets: 'sport', 'clinic' or null (learning wording as written). */
export function termSetFor(centerType, learningSubType) {
  const type = centerType || 'learning';
  if (type === 'learning') return learningSubType === 'sport' ? 'sport' : null;
  if (type === 'therapy' || type === 'hair_transplant' || type === 'pharma_clinic') return 'clinic';
  return null;
}

const RULES = { sport: SPORT, clinic: CLINIC };

// Two rules can meet on one phrase ("الحصص التدريبية", "Lesson session"):
// collapse the doubled word they leave behind.
const TIDY = [
  { re: /(التدريبية|تدريبية)( \1)+/g, to: '$1' },
  { re: /\b([Tt]raining) training\b/g, to: '$1' },
  { re: /\b([Tt]raining sessions?) training sessions?\b/g, to: '$1' },
  { re: /(אימונים|אימון)( \1)+/g, to: '$1' },
  { re: /(متدربين|المتدربين) \/ \1/g, to: '$1' },
  { re: /\b(trainees|clients) \/ \1\b/g, to: '$1' },
  { re: /(מתאמנים|לקוחות) \/ \1/g, to: '$1' },
];

// Sections that are not about the center's own people: the public marketing
// site describes every center type at once, and the sub-type picker in
// Settings describes each type in its own words.
const SKIP_TOP = new Set(['website']);
const SKIP_KEYS = new Set([
  'quran.settings.teachingDesc', 'quran.settings.quranDesc', 'caffe.settings.caffeDesc', 'sport.settings.sportDesc',
]);

export function rewriteText(text, set, lng) {
  const rules = RULES[set] && RULES[set][String(lng || '').slice(0, 2)];
  if (!rules || typeof text !== 'string' || !text) return text;
  // Keep {{placeholders}} and $t(nesting) untouched.
  const parts = text.split(/(\{\{[^}]*\}\}|\$t\([^)]*\))/);
  return parts
    .map((p, i) => (i % 2 ? p : [...rules, ...TIDY].reduce((s, r) => s.replace(r.re, r.to), p)))
    .join('');
}

export function rewriteBundle(bundle, set, lng, path = '') {
  if (!bundle || typeof bundle !== 'object') return bundle;
  const out = Array.isArray(bundle) ? [] : {};
  for (const [k, v] of Object.entries(bundle)) {
    const key = path ? `${path}.${k}` : k;
    if ((!path && SKIP_TOP.has(k)) || SKIP_KEYS.has(key)) { out[k] = v; continue; }
    out[k] = typeof v === 'string' ? rewriteText(v, set, lng) : rewriteBundle(v, set, lng, key);
  }
  return out;
}

/**
 * Keep the loaded bundles in the wording of `set`. Originals are kept per
 * language so a later switch (or set === null) restores them exactly.
 */
export function createCenterTerms(i18n, ns = 'translation') {
  const originals = new Map(); // lng -> pristine bundle
  let current = null;

  const applyTo = (lng) => {
    const loaded = i18n.getResourceBundle(lng, ns);
    if (!loaded) return;
    if (!originals.has(lng)) originals.set(lng, JSON.parse(JSON.stringify(loaded)));
    const base = originals.get(lng);
    const next = current ? rewriteBundle(base, current, lng) : base;
    i18n.addResourceBundle(lng, ns, next, true, true);
  };

  const refresh = () => {
    Object.keys(i18n.store?.data || {}).forEach(applyTo);
    // Re-render consumers of the same language.
    i18n.emit('languageChanged', i18n.language);
  };

  // A language loaded later (lazy chunk) gets the current wording too.
  i18n.store?.on?.('added', (lng, addedNs) => {
    if (addedNs !== ns || originals.has(lng)) return;
    applyTo(lng);
  });

  return {
    set(nextSet) {
      if (nextSet === current) return;
      current = nextSet;
      refresh();
    },
    get current() { return current; },
  };
}
